import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Footer } from "../components/Footer";
import { usePlatformAuth } from "../context/platform-auth";

type InvitationDetails = {
	workspaceName: string;
	workspaceSlug: string;
	inviterName: string;
	inviterEmail?: string;
	email: string;
	role: string;
	status: string;
};

type ErrorState = {
	type: "not_found" | "expired" | "network";
	message: string;
};

export function InviteAcceptPage() {
	const { token } = useParams<{ token: string }>();
	const navigate = useNavigate();
	const { user, loading: authLoading } = usePlatformAuth();

	const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<ErrorState | null>(null);
	const [accepting, setAccepting] = useState(false);
	const [acceptError, setAcceptError] = useState("");

	useEffect(() => {
		if (!token) return;

		async function fetchInvitation() {
			try {
				const res = await fetch(`/api/invitations/${token}`, {
					credentials: "include",
				});

				if (!res.ok) {
					const body = (await res.json().catch(() => null)) as {
						error?: string;
						message?: string;
					} | null;
					setError({
						type: body?.error === "expired" ? "expired" : "not_found",
						message: body?.message ?? "This invitation is no longer valid.",
					});
					return;
				}

				const data = (await res.json()) as InvitationDetails;
				setInvitation(data);
			} catch {
				setError({
					type: "network",
					message: "Failed to load invitation details. Please try again.",
				});
			} finally {
				setLoading(false);
			}
		}

		fetchInvitation();
	}, [token]);

	async function handleAccept() {
		if (!token) return;
		setAccepting(true);
		setAcceptError("");

		try {
			const res = await fetch(`/api/invitations/${token}/accept`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
			});

			const body = (await res.json()) as {
				redirectTo?: string;
				error?: string;
				message?: string;
			};

			if (!res.ok) {
				if (body.error === "email_mismatch") {
					setAcceptError(body.message ?? "Email mismatch");
				} else if (body.error === "expired") {
					setError({
						type: "expired",
						message:
							body.message ??
							"This invitation has expired. Ask the workspace admin for a new invite.",
					});
				} else {
					setAcceptError(body.message ?? "Failed to accept invitation");
				}
				return;
			}

			if (body.error === "already_member") {
				navigate(body.redirectTo ?? "/workspaces", { replace: true });
				return;
			}

			navigate(body.redirectTo ?? "/workspaces", { replace: true });
		} catch {
			setAcceptError("Network error. Please try again.");
		} finally {
			setAccepting(false);
		}
	}

	if (loading || authLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<p className="text-gray-500">Loading invitation...</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className="min-h-screen flex flex-col bg-gray-50">
				<main className="flex-1 flex items-center justify-center">
					<div className="w-full max-w-md bg-white rounded-lg shadow px-6 py-8 text-center">
						{error.type === "expired" ? (
							<>
								<h1 className="text-2xl font-bold text-gray-900 mb-4">
									Invitation Expired
								</h1>
								<p className="text-gray-600 mb-6">{error.message}</p>
							</>
						) : (
							<>
								<h1 className="text-2xl font-bold text-gray-900 mb-4">
									Invitation Not Found
								</h1>
								<p className="text-gray-600 mb-6">{error.message}</p>
							</>
						)}
						<Link
							to="/workspaces"
							className="text-blue-600 hover:underline text-sm"
						>
							Go to workspaces
						</Link>
					</div>
				</main>
				<Footer />
			</div>
		);
	}

	if (!invitation) return null;

	const emailMismatch =
		user && user.email.toLowerCase() !== invitation.email.toLowerCase();

	return (
		<div className="min-h-screen flex flex-col bg-gray-50">
			<header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
				<a href="/workspaces" className="text-lg font-semibold text-gray-900">
					Apsu
				</a>
				{user && <span className="text-sm text-gray-600">{user.email}</span>}
			</header>
			<main className="flex-1 flex items-center justify-center">
				<div className="w-full max-w-md bg-white rounded-lg shadow px-6 py-8">
					<h1 className="text-2xl font-bold text-gray-900 mb-2">
						You've been invited
					</h1>
					<p className="text-gray-600 mb-6">
						<span className="font-medium">{invitation.inviterName}</span>{" "}
						invited you to join{" "}
						<span className="font-semibold">{invitation.workspaceName}</span> as
						a {invitation.role}.
					</p>

					{!user && (
						<div className="space-y-3">
							<p className="text-sm text-gray-500 mb-4">
								Sign in or create an account to accept this invitation.
							</p>
							<Link
								to={`/sign-in?redirect=${encodeURIComponent(`/invite/${token}`)}`}
								className="block w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white text-center hover:bg-blue-700"
							>
								Sign In
							</Link>
							<Link
								to={`/sign-up?redirect=${encodeURIComponent(`/invite/${token}`)}`}
								className="block w-full rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 text-center hover:bg-gray-50"
							>
								Create an Account
							</Link>
						</div>
					)}

					{user && emailMismatch && (
						<div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
							<p className="text-sm text-yellow-800">
								This invitation was sent to{" "}
								<span className="font-medium">{invitation.email}</span>, but
								you're signed in as{" "}
								<span className="font-medium">{user.email}</span>. Contact the
								workspace admin.
							</p>
						</div>
					)}

					{user && !emailMismatch && (
						<div className="space-y-3">
							{acceptError && (
								<p className="text-red-600 text-sm">{acceptError}</p>
							)}
							<button
								type="button"
								onClick={handleAccept}
								disabled={accepting}
								className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
							>
								{accepting ? "Accepting..." : "Accept Invitation"}
							</button>
						</div>
					)}
				</div>
			</main>
			<Footer />
		</div>
	);
}
